import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Card, CardContent, CardHeader, Typography } from '@mui/material';

import { useAPIAuthenticatedService } from '../../helpers/APICallsAuthenticated';
import { CacheKeys } from '../../helpers/DataStructures';
import CreateOfferForm from '../../forms/CreateOfferForm';
import AsyncActionButton from '../../components/AsyncActionButton';
import InHouseDialog from '../../components/InHouseDialog';

const ReferralsAppOfferManagment: React.FC = () => {
  const apiServiceInstance = useAPIAuthenticatedService();
  const queryClient = useQueryClient();

  const { isLoading, isError, data } = useQuery([CacheKeys.LOGGED_IN_USERS_OFFER], async () => {
    const user = await apiServiceInstance.getOffersForAuthenticatedUser();
    return user;
  });

  useEffect(() => {
    if (data) {
      setEditMode(false);
    }
  }, [data]);

  const mutation = useMutation(() => apiServiceInstance.withdrawOfferForLoggedInUser(), {
    onSuccess: () => {
      queryClient.invalidateQueries([CacheKeys.LOGGED_IN_USERS_OFFER]);
    },
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const handleWithdraw = () => {
    setDialogOpen(true);
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
  };

  return (
    <Card style={{ margin: '0 auto' }} title="dd" variant="outlined">
      {isLoading ? (
        <CardHeader
          sx={{ textAlign: 'center', backgroundColor: '#333', margin: '10px', padding: '2px' }}
          title="Loading..."
        />
      ) : isError ? (
        <CardHeader
          sx={{ textAlign: 'center', backgroundColor: '#333', margin: '10px', padding: '2px' }}
          title="Error"
        />
      ) : !data ? (
        <>
          <CardHeader
            sx={{ textAlign: 'center', backgroundColor: '#333', margin: '10px', padding: '2px' }}
            title="Create an offer"
          />
          <CardContent>
            <CreateOfferForm mode="Create" />
          </CardContent>
        </>
      ) : (
        <>
          <CardHeader
            sx={{ textAlign: 'center', backgroundColor: '#333', margin: '10px', padding: '2px' }}
            title="Your Offer"
          />
          <CardContent style={{ textAlign: 'center' }}>
            {!editMode && (
              <>
                {data.offeringFixedCashAmount === 0 && (
                  <Typography mb={2} variant="body1">
                    You are not currently offering a reward for successful referrals
                  </Typography>
                )}
                {data.offeringFixedCashAmount > 0 && (
                  <Typography mb={2} variant="body1">
                    You are offering{' '}
                    <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      £{data.offeringFixedCashAmount}
                    </Box>{' '}
                    for each successful referral
                  </Typography>
                )}

                <Typography variant="body2">Area covered:</Typography>
                <Typography mb={2} variant="body1">
                  {data.location}
                </Typography>

                {data.theOffer && (
                  <Typography mb={2} variant="body2">
                    {data.theOffer}
                  </Typography>
                )}

                {/* REQ-024: dashboard offer actions should use the shared quiet primary/destructive styling. */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    mt: 1,
                  }}
                >
                  <Button color="primary" onClick={handleEdit} variant="quiet">
                    Change your offer
                  </Button>
                  <Button color="secondary" onClick={handleWithdraw} variant="quiet">
                    Suspend your offer
                  </Button>
                </Box>
              </>
            )}

            {editMode && (
              <CreateOfferForm
                extraButton={
                  <Button
                    color="secondary"
                    onClick={handleCancelEdit}
                    sx={{ ml: 1 }}
                    variant="quiet"
                  >
                    Cancel
                  </Button>
                }
                mode="Edit"
              />
            )}
          </CardContent>

          <InHouseDialog
            actions={
              <AsyncActionButton
                autoFocus
                disabled={mutation.isLoading}
                errorToast="Error"
                idleLabel="Confirm"
                onAction={async () => {
                  await mutation.mutateAsync();
                }}
                onAfterSuccessDisplay={() => {
                  setDialogOpen(false);
                }}
                pendingLabel="Suspending..."
                styleVariant="red"
                successDisplayMs={250}
                successLabel="Offer Suspended"
                successToast="Offer withdrawn successfully"
              />
            }
            closeDisabled={mutation.isLoading}
            closeLabel="Cancel"
            contentVariant="message"
            onClose={() => setDialogOpen(false)}
            open={dialogOpen}
            title="Confirm Withdrawal"
          >
            Are you sure you want to withdraw your offer?
          </InHouseDialog>
        </>
      )}
    </Card>
  );
};

export default ReferralsAppOfferManagment;
